import React, { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AdminProvider } from "./context/AdminContext";
import { AuthProvider } from "./context/AuthContext";
import AppRoutes from "./routes";
import PerformanceMonitor from "./components/common/PerformanceMonitor";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { preloadCriticalLibraries } from "./utils/dynamicImports";
import { registerServiceWorker } from "./utils/cache/serviceWorker";
import { applySecurityHeaders } from "./utils/security/headers";
import { resolveRpcUrl } from "./services/rpc/rpcUrl";
import { resolveGatewayUrl } from "./services/sso/gatewayUrl";
import "./styles/theme-variables.css";
import "./styles/admin-theme-variables.css";

const App: React.FC = () => {
  useEffect(() => {
    preloadCriticalLibraries();
    registerServiceWorker();
    // connect-src must admit the runtime-configured Lane A /rpc origin and
    // the NU GUI gateway broker origin (#590/#940), so the policy is applied
    // once both are resolved. A failed lookup still applies the base policy.
    const dev = process.env.NODE_ENV === "development";
    void Promise.all([
      resolveRpcUrl().catch(() => null),
      resolveGatewayUrl().catch(() => null),
    ]).then(([rpcUrl, gatewayUrl]) => {
      const origins: string[] = [];
      try {
        if (rpcUrl) origins.push(new URL(rpcUrl).origin);
      } catch {
        // unusable URL: leave it out of the policy
      }
      if (gatewayUrl) origins.push(gatewayUrl);
      applySecurityHeaders(dev, origins);
    });
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AdminProvider>
            <BrowserRouter>
              <ErrorBoundary
                fallback={
                  <div className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                      <h2 className="text-xl font-semibold mb-2">
                        Navigation Error
                      </h2>
                      <p className="text-gray-600">
                        Please refresh the page to continue.
                      </p>
                    </div>
                  </div>
                }
              >
                <AppRoutes />
              </ErrorBoundary>
              {/* Dev-only overlay: the component's own defaults gate it to
                NODE_ENV=development (+ Ctrl+Shift+P toggle). The previous
                hardcoded enabled/showDevTools props forced the devtools
                widget into the PRODUCTION bundle on every screen (visible
                in the #601 fidelity captures vs the goldens). */}
              <PerformanceMonitor />
            </BrowserRouter>
          </AdminProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
